// @givmo/board — the officer's "Annual disclosures" part of the Conflicts of interest page (CBG-YR2-WEB1;
// frames o1-before, o3-dashboard, and the dialogs o2-start, o4-remind, o5-close).
//
// ══ WHAT THE PART MUST KEEP (the derivation §2.8–§2.11) ═════════════════════════════════════════
//
//   * ONE COUNT, OF SUBMISSIONS, in words ("4 of 7 submitted"), with no bar: a mechanical fact about
//     the year. Never a count of conflicts, anywhere.
//   * STATUS AS PLAIN WORDS, colour only where the officer has something to do (a joiner not sent
//     yet). No "overdue", no "late", no red, no countdown — after the due date the date simply stays.
//   * NOTHING SCHEDULES ANYTHING. Reminders go when the officer presses Remind, at most once a day to
//     each person (the backend's own rule, `can_remind`); before a year starts there is no nudge
//     about when to run one (§3.2, §3.6).
//   * Every act is `board_conflict_disclosure:manage` (an Owner's): a reader without it sees the year,
//     its answers and its PDFs, and is offered no act at all.
//
// ══ THE LATEST CLOSED YEAR KEEPS ITS TABLE (CBG-YR2-WEB3; frames y1–y3) ═════════════════════════
//
// Until a later year opens, the backend still takes a closed year's first answers, as received
// after the close (contract §5.6, Q7), and corrections from paper (§5.5) — and a person with no
// account can answer only on paper. So the latest year keeps its table once it closes, read-only
// apart from those paper acts: no Remind, Send, Close, due date or Withdraw, and its menu keeps the
// download. "Latest" is the backend's own rule: the most recently opened year not withdrawn. The
// older closed years stay cards.

import { useState } from 'react';
import { Mail, MoreHorizontal, Send } from 'lucide-react';
import type {
  BoardCoiCycleOut,
  BoardCoiCyclePersonOut,
  BoardCoiCycleSummaryOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  DataTable,
  Icon,
  IconButton,
  Menu,
  RowActions,
  Skeleton,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import { GroupLabel, HowThisWorks, StatusWord } from '../../components/fine-print';
import { closeRecordDay, displayDate } from '../../lib/display';
import { BOARD_PAGES } from '../../board-routes';
import { Link, useNavigate } from '../../board-routing';
import { useBoardOperations } from '../../board-runtime';
import {
  capacityOf,
  offersPaperForm,
  previousCycleOf,
  remindablePeople,
  shortDay,
  statusOf,
} from './coi-decode';
import { pdfName, readerTimeZone, savePdf } from './coi-download';
import {
  CloseDialog,
  DueDateDialog,
  RemindDialog,
  SendDialog,
  SendJoinersDialog,
  WithdrawDialog,
} from './coi-officer-dialogs';
import { PaperFormDialog } from './coi-paper-dialog';
import { PersonCell } from './coi-parts';
import { headResponse, useCoiCycle, useCoiHeadResponse } from './coi-queries';
import { StartDialog } from './coi-start-dialog';
import { coiWords as w } from './coi-words';

const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';

type Open =
  | { kind: 'start' }
  | { kind: 'remind'; people: BoardCoiCyclePersonOut[] }
  | { kind: 'send'; person: BoardCoiCyclePersonOut }
  // CBG-YR4-COI-JOINER: the closed year sent to everyone who joined after it closed.
  | { kind: 'send-joiners' }
  | { kind: 'close' }
  | { kind: 'due' }
  | { kind: 'withdraw' }
  | { kind: 'paper'; person: BoardCoiCyclePersonOut }
  | { kind: 'correct'; person: BoardCoiCyclePersonOut }
  | null;

export function AnnualPart({
  orgRef,
  cycles,
  canManage,
  organization,
}: {
  orgRef: string;
  cycles: readonly BoardCoiCycleSummaryOut[];
  canManage: boolean;
  organization: string;
}) {
  const [open, setOpen] = useState<Open>(null);
  const current = cycles.find((cycle) => cycle.status === 'open') ?? null;
  const latestClosed = current === null ? latestClosedYear(cycles) : null;
  const earlier = cycles.filter(
    (cycle) => cycle.status === 'closed' && cycle.id !== latestClosed?.id,
  );

  return (
    <div className="flex flex-col gap-24 pt-8" data-testid="coi-annual">
      {current !== null ? (
        <YearDashboard
          orgRef={orgRef}
          summary={current}
          cycles={cycles}
          canManage={canManage}
          organization={organization}
          open={open}
          setOpen={setOpen}
        />
      ) : (
        <BeforeCard
          canManage={canManage}
          label={null}
          hasEarlier={earlier.length > 0 || latestClosed !== null}
          cycles={cycles}
          onStart={() => setOpen({ kind: 'start' })}
        />
      )}

      {latestClosed !== null ? (
        <YearDashboard
          orgRef={orgRef}
          summary={latestClosed}
          cycles={cycles}
          canManage={canManage}
          organization={organization}
          open={open}
          setOpen={setOpen}
        />
      ) : null}

      {earlier.length > 0 ? (
        <section className="flex flex-col gap-10" data-testid="coi-earlier">
          <GroupLabel>{w.earlierYears}</GroupLabel>
          {earlier.map((cycle) => (
            <Card key={cycle.id} testId={`coi-earlier-${cycle.id}`}>
              <CardBody className="flex flex-wrap items-center justify-between gap-8">
                <span className="flex flex-col gap-2">
                  <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                    {w.yearTitle(cycle.label)}
                  </span>
                  <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                    {w.closedLine(
                      // The day the close's record is dated by, as the record says it (review-180's F5).
                      displayDate(closeRecordDay(cycle.closed_at)),
                      cycle.closed_by_display_name ?? null,
                      cycle.submitted_count,
                      cycle.people_count,
                    )}
                  </span>
                </span>
                {cycle.close_record_id ? (
                  <Link
                    to={BOARD_PAGES.minuteBook + '/$recordId'}
                    params={{ recordId: cycle.close_record_id }}
                    className={LINK}
                    data-testid={`coi-earlier-record-${cycle.id}`}
                  >
                    {w.inMinuteBook}
                  </Link>
                ) : null}
              </CardBody>
            </Card>
          ))}
        </section>
      ) : null}

      {open?.kind === 'start' ? (
        <StartDialog
          orgRef={orgRef}
          cycles={cycles}
          organization={organization}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * The latest year when it is closed and no later one has opened (contract §5.6: a withdrawn year
 * never counts), whose table stays; else null. Years are compared by when they were opened.
 */
export function latestClosedYear(
  cycles: readonly BoardCoiCycleSummaryOut[],
): BoardCoiCycleSummaryOut | null {
  const latest =
    [...cycles]
      .filter((cycle) => cycle.status !== 'cancelled')
      .sort((a, b) => (b.opened_at ?? '').localeCompare(a.opened_at ?? ''))[0] ?? null;
  return latest?.status === 'closed' ? latest : null;
}

/** Before a year is started (o1-before): one line and the act, and no word about when. */
function BeforeCard({
  canManage,
  hasEarlier,
  cycles,
  onStart,
}: {
  canManage: boolean;
  label: string | null;
  hasEarlier: boolean;
  cycles: readonly BoardCoiCycleSummaryOut[];
  onStart: () => void;
}) {
  if (!canManage) {
    return hasEarlier ? null : (
      <Card testId="coi-before">
        <CardBody>
          <p className={SECONDARY}>{w.noYearYet}</p>
        </CardBody>
      </Card>
    );
  }
  const year = String(new Date().getFullYear());
  const taken = new Set(
    cycles.filter((cycle) => cycle.status !== 'cancelled').map((cycle) => cycle.label),
  );
  const label = taken.has(year) ? String(Number(year) + 1) : year;
  return (
    <Card testId="coi-before">
      <CardBody className="flex flex-col items-start gap-12">
        <p className={SECONDARY}>
          {w.beforeLead(label)} <HowThisWorks testId="coi-before-how" lines={w.beforeHow} />
        </p>
        <Button
          appearance="saas"
          tone="primary"
          leadingIcon={<Icon icon={Send} size={16} aria-hidden />}
          onClick={onStart}
          testId="coi-start"
        >
          {w.startYear(label)}
        </Button>
      </CardBody>
    </Card>
  );
}

/** The open year (o3-dashboard), or the latest closed one, read-only apart from its paper acts (y1). */
function YearDashboard({
  orgRef,
  summary,
  cycles,
  canManage,
  organization,
  open,
  setOpen,
}: {
  orgRef: string;
  summary: BoardCoiCycleSummaryOut;
  cycles: readonly BoardCoiCycleSummaryOut[];
  canManage: boolean;
  organization: string;
  open: Open;
  setOpen: (next: Open) => void;
}) {
  const operations = useBoardOperations();
  const navigate = useNavigate();
  const query = useCoiCycle(orgRef, summary.id);
  const cycle = query.data ?? null;
  const previous = previousCycleOf(cycles, summary);
  const previousLabel = previous?.label ?? null;

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-12" data-testid="coi-year-loading">
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-84 w-full rounded-lg" />
      </div>
    );
  }
  if (cycle === null) {
    return (
      <Card testId="coi-year-error">
        <CardBody className="flex flex-col items-start gap-12">
          <p className={SECONDARY}>{w.yearUnavailable}</p>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={() => void query.refetch()}
            testId="coi-year-retry"
          >
            {w.tryAgain}
          </Button>
        </CardBody>
      </Card>
    );
  }

  const yearOpen = cycle.status === 'open';
  const remindable = yearOpen ? remindablePeople(cycle.people) : [];
  const openResponse = (recordId: string, view: 'answers' | 'changes') =>
    void navigate({
      to: BOARD_PAGES.coiResponse,
      params: { cycleId: cycle.id, recordId },
      ...(view === 'changes' ? { search: { view: 'changes' } } : {}),
    });

  const download = async (person: BoardCoiCyclePersonOut) => {
    try {
      const head = await headResponse(
        operations,
        orgRef,
        cycle.id,
        person.response_record_id ?? '',
      );
      if (head === null) throw new Error('none');
      const { data } = await operations.officerGetBoardCoiResponseDocument({
        path: { org_ref: orgRef, cycle_id: cycle.id, record_id: head.record_id },
        query: { view: 'clean', time_zone: readerTimeZone() },
        throwOnError: true,
      });
      savePdf(data, pdfName(person.display_name, cycle.label, 'conflicts of interest form'));
    } catch {
      toast.error(w.couldNotDownload);
    }
  };
  const downloadAll = async () => {
    try {
      const { data } = await operations.officerGetBoardCoiCycleDocument({
        path: { org_ref: orgRef, cycle_id: cycle.id },
        query: { time_zone: readerTimeZone() },
        throwOnError: true,
      });
      savePdf(data, pdfName(cycle.label, 'conflicts of interest forms'));
    } catch {
      toast.error(w.couldNotDownload);
    }
  };
  const printPaper = async (person: BoardCoiCyclePersonOut) => {
    try {
      const { data } = await operations.officerGetBoardCoiPaperForm({
        path: { org_ref: orgRef, cycle_id: cycle.id },
        query: { board_member_id: person.board_member_id },
        throwOnError: true,
      });
      savePdf(data, pdfName(person.display_name, cycle.label, 'paper form'));
    } catch {
      toast.error(w.couldNotDownload);
    }
  };

  const columns: DataTableColumn<BoardCoiCyclePersonOut>[] = [
    {
      id: 'person',
      header: w.personColumn,
      cell: (row) => <PersonCell name={row.display_name ?? ''} capacity={capacityOf(row)} />,
      stacked: 'heading',
    },
    {
      id: 'status',
      header: w.statusColumn,
      cell: (row) => {
        const status = statusOf(row, yearOpen);
        return (
          <span className="flex flex-col items-start gap-2">
            <StatusWord tone={status.tone} testId={`coi-status-${row.board_member_id}`}>
              {status.word}
            </StatusWord>
            {status.notes.map((note) => (
              <span
                key={note}
                className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
              >
                {note}
              </span>
            ))}
          </span>
        );
      },
      width: '248px',
      stacked: 'line',
      stackedClassName: 'max-sm:mt-6',
    },
    {
      id: 'changes',
      header: w.changesColumn(previousLabel),
      stackedLabel: `${w.changesColumn(previousLabel)}:`,
      cell: (row) =>
        row.status !== 'submitted' && row.status !== 'left' ? null : row.changes === 'changes' &&
          row.response_record_id ? (
          <button
            type="button"
            className={LINK}
            onClick={(event) => {
              event.stopPropagation();
              openResponse(row.response_record_id ?? '', 'changes');
            }}
            data-testid={`coi-changes-${row.board_member_id}`}
          >
            {w.seeChanges}
          </button>
        ) : row.changes === 'none' ? (
          <span className="text-text-secondary">{w.noChanges}</span>
        ) : row.changes === 'first' ? (
          <span className="text-text-secondary">{w.firstYear}</span>
        ) : null,
      width: '200px',
      stacked: 'line',
      stackedClassName: 'max-sm:mt-4',
    },
    {
      id: 'act',
      header: <span className="sr-only">{w.actionsColumn}</span>,
      cell: (row) => (
        <RowAct
          row={row}
          yearOpen={yearOpen}
          canManage={canManage}
          onOpenResponse={openResponse}
          onDownload={() => void download(row)}
          onPrint={() => void printPaper(row)}
          setOpen={setOpen}
        />
      ),
      width: '160px',
      align: 'right',
      stacked: 'line',
    },
  ];

  return (
    <section className="flex flex-col gap-16" data-testid="coi-year">
      <div className="flex flex-wrap items-start justify-between gap-12">
        <div className="flex min-w-0 flex-col gap-4">
          <h2 className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {w.yearTitle(cycle.label)}
          </h2>
          {yearOpen ? (
            <p className={SECONDARY} data-testid="coi-year-facts">
              {w.yearFacts(
                shortDay(cycle.due_on),
                cycle.period_label,
                shortDay(cycle.opened_at),
                cycle.opened_by_display_name ?? null,
              )}{' '}
              <HowThisWorks testId="coi-year-how" lines={w.yearHow} />
            </p>
          ) : (
            <>
              {/* The closed year's card line, its count included, and its record (y1). */}
              <p className={SECONDARY} data-testid="coi-year-facts">
                {w.closedLine(
                  // The day the close's record is dated by, as the record says it (review-180's F5).
                  displayDate(closeRecordDay(cycle.closed_at)),
                  cycle.closed_by_display_name ?? null,
                  cycle.submitted_count,
                  cycle.people_count,
                )}
                {cycle.close_record_id ? (
                  <>
                    {' · '}
                    <Link
                      to={BOARD_PAGES.minuteBook + '/$recordId'}
                      params={{ recordId: cycle.close_record_id }}
                      className={`${LINK} whitespace-nowrap`}
                      data-testid="coi-year-record"
                    >
                      {w.inMinuteBook}
                    </Link>
                  </>
                ) : null}
              </p>
              {/* Why the table is still here: the close dialog's own line, to whoever can act. */}
              {canManage ? (
                <p
                  className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                  data-testid="coi-year-late"
                >
                  {w.closeLate}
                </p>
              ) : null}
            </>
          )}
        </div>
        <div className="flex items-center gap-8">
          {canManage && remindable.length > 0 ? (
            <Button
              appearance="saas"
              tone="secondary"
              leadingIcon={<Icon icon={Mail} size={16} aria-hidden />}
              onClick={() => setOpen({ kind: 'remind', people: remindable })}
              testId="coi-remind"
            >
              {w.remindPeople(remindable.length)}
            </Button>
          ) : null}
          <Menu.Root>
            <Menu.Trigger
              render={
                <IconButton
                  aria-label={w.yearMenu(cycle.label)}
                  tone="secondary"
                  size="md"
                  testId="coi-year-menu"
                />
              }
            >
              <Icon icon={MoreHorizontal} size={18} aria-hidden />
            </Menu.Trigger>
            <Menu.Content saas align="end" className="min-w-[248px]" testId="coi-year-menu-content">
              <Menu.Item saas onClick={() => void downloadAll()} testId="coi-download-all">
                {w.downloadAll}
              </Menu.Item>
              {canManage && yearOpen ? (
                <>
                  <Menu.Item saas onClick={() => setOpen({ kind: 'due' })} testId="coi-change-due">
                    {w.changeDue}
                  </Menu.Item>
                  <Menu.Item
                    saas
                    onClick={() => setOpen({ kind: 'close' })}
                    testId="coi-close-open"
                  >
                    {w.closeYear(cycle.label)}
                  </Menu.Item>
                  {cycle.submitted_count === 0 ? (
                    <Menu.Item
                      saas
                      onClick={() => setOpen({ kind: 'withdraw' })}
                      testId="coi-withdraw-open"
                    >
                      {w.withdrawYear(cycle.label)}
                    </Menu.Item>
                  ) : null}
                </>
              ) : null}
            </Menu.Content>
          </Menu.Root>
        </div>
      </div>

      {/* The year's one count, in words: a bar adds weight, not a fact (the orchestrator's mark). A
          closed year's line above carries it. */}
      {yearOpen ? (
        <p
          className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
          data-testid="coi-count"
        >
          <span className="font-w600 text-text-strong">
            {w.submittedOf(cycle.submitted_count, cycle.people_count)}
          </span>{' '}
          {w.submittedWord}
        </p>
      ) : null}

      <Card className="overflow-hidden" testId="coi-recipients">
        <DataTable
          columns={columns}
          rows={cycle.people.filter((row) => !row.joined_after_close)}
          rowKey={(row) => row.board_member_id}
          rowTestId={(row) => `coi-row-${row.board_member_id}`}
          stackAt="sm"
        />
      </Card>

      {/* CBG-YR4-COI-JOINER: the members who joined after the year closed, as their own group with
          their Send — outside the year's own count, which stays the year's. */}
      {cycle.people.some((row) => row.joined_after_close) ? (
        <section className="flex flex-col gap-10" data-testid="coi-joiners">
          <div className="flex flex-wrap items-end justify-between gap-12">
            <div className="flex max-w-[72ch] flex-col gap-4">
              <h3 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                {w.joinersGroup}
              </h3>
              <p className={SECONDARY}>{w.joinersLine(cycle.label)}</p>
            </div>
            {canManage && (cycle.joiners_sendable_count ?? 0) > 1 ? (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => setOpen({ kind: 'send-joiners' })}
                testId="coi-send-joiners"
              >
                {w.sendJoiners(cycle.label)}
              </Button>
            ) : null}
          </div>
          <Card className="overflow-hidden" testId="coi-joiner-rows">
            <DataTable
              columns={columns}
              rows={cycle.people.filter((row) => row.joined_after_close)}
              rowKey={(row) => row.board_member_id}
              rowTestId={(row) => `coi-row-${row.board_member_id}`}
              stackAt="sm"
            />
          </Card>
        </section>
      ) : null}

      {open?.kind === 'remind' ? (
        <RemindDialog
          orgRef={orgRef}
          cycle={cycle}
          people={open.people}
          organization={organization}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open?.kind === 'send' ? (
        <SendDialog
          orgRef={orgRef}
          cycle={cycle}
          person={open.person}
          organization={organization}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open?.kind === 'send-joiners' ? (
        <SendJoinersDialog orgRef={orgRef} cycle={cycle} onClose={() => setOpen(null)} />
      ) : null}
      {open?.kind === 'close' ? (
        <CloseDialog orgRef={orgRef} cycle={cycle} onClose={() => setOpen(null)} />
      ) : null}
      {open?.kind === 'due' ? (
        <DueDateDialog orgRef={orgRef} cycle={cycle} onClose={() => setOpen(null)} />
      ) : null}
      {open?.kind === 'withdraw' ? (
        <WithdrawDialog orgRef={orgRef} cycle={cycle} onClose={() => setOpen(null)} />
      ) : null}
      {open?.kind === 'paper' ? (
        <PaperFormDialog
          orgRef={orgRef}
          cycleId={cycle.id}
          seatId={open.person.board_member_id}
          name={open.person.display_name ?? ''}
          correcting={null}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open?.kind === 'correct' ? (
        <CorrectFromPaper
          orgRef={orgRef}
          cycle={cycle}
          person={open.person}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </section>
  );
}

/** A correction from paper: the answers in force first, then the form over them. */
function CorrectFromPaper({
  orgRef,
  cycle,
  person,
  onClose,
}: {
  orgRef: string;
  cycle: BoardCoiCycleOut;
  person: BoardCoiCyclePersonOut;
  onClose: () => void;
}) {
  const head = useCoiHeadResponse(orgRef, cycle.id, person.response_record_id ?? null);
  if (head.data === undefined) return null;
  return (
    <PaperFormDialog
      orgRef={orgRef}
      cycleId={cycle.id}
      seatId={person.board_member_id}
      name={person.display_name ?? ''}
      correcting={head.data}
      onClose={onClose}
    />
  );
}

/** A row's acts: Send for a joiner; a menu for everyone else. */
function RowAct({
  row,
  yearOpen,
  canManage,
  onOpenResponse,
  onDownload,
  onPrint,
  setOpen,
}: {
  row: BoardCoiCyclePersonOut;
  yearOpen: boolean;
  canManage: boolean;
  onOpenResponse: (recordId: string, view: 'answers' | 'changes') => void;
  onDownload: () => void;
  onPrint: () => void;
  setOpen: (next: Open) => void;
}) {
  const name = row.display_name ?? '';
  const answered = row.response_record_id !== null && row.response_record_id !== undefined;
  const actions = answered
    ? [
        {
          label: w.seeAnswers,
          onSelect: () => onOpenResponse(row.response_record_id ?? '', 'answers'),
        },
        { label: w.downloadOne, onSelect: onDownload },
        ...(canManage
          ? [
              {
                label: w.correctFromPaper,
                onSelect: () => setOpen({ kind: 'correct', person: row }),
              },
            ]
          : []),
      ]
    : [
        ...(canManage && yearOpen && row.can_remind
          ? [{ label: w.remind, onSelect: () => setOpen({ kind: 'remind', people: [row] }) }]
          : []),
        ...(canManage && offersPaperForm(row, yearOpen)
          ? [{ label: w.recordPaper, onSelect: () => setOpen({ kind: 'paper', person: row }) }]
          : []),
        // The form to print is the one the paper road takes: the same people.
        ...(offersPaperForm(row, yearOpen) ? [{ label: w.printPaper, onSelect: onPrint }] : []),
      ];
  return (
    <span className="flex items-center justify-end gap-6 max-sm:absolute max-sm:right-8 max-sm:top-8">
      {canManage &&
      (yearOpen || row.joined_after_close === true) &&
      row.status === 'not_sent' &&
      row.can_send ? (
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          leadingIcon={<Icon icon={Send} size={14} aria-hidden />}
          onClick={() => setOpen({ kind: 'send', person: row })}
          testId={`coi-send-${row.board_member_id}`}
        >
          {w.send}
        </Button>
      ) : null}
      {actions.length > 0 ? (
        <RowActions
          testId={`coi-actions-${row.board_member_id}`}
          label={w.actionsFor(name)}
          actions={actions}
        />
      ) : null}
    </span>
  );
}
